import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../axiosInstance';
import { LanguageContext } from '../LanguageContext';
import useWafCaptcha from '../hooks/useWafCaptcha';
import useCapsLockWarning from '../hooks/useCapsLockWarning';
import CapsLockWarning from '../components/CapsLockWarning';

const COPY = {
  en: {
    skip: 'Skip to main content',
    heading: 'Change your password',
    intro: 'Enter your current password, then choose a new password that you can keep private and remember.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    hint: 'Use at least 8 characters with an upper and lower case letter, a number, and a symbol.',
    submit: 'Change password',
    success: 'Your password has been updated.',
    back: 'Return to dashboard',
    errorMatch: 'Passwords do not match.',
    errorLength: 'Password must be at least 8 characters.',
    errorGeneric: 'Failed to change password.'
  },
  fr: {
    skip: 'Passer au contenu principal',
    heading: 'Changer votre mot de passe',
    intro: 'Entrez votre mot de passe actuel, puis choisissez un nouveau mot de passe que vous pourrez garder confidentiel et retenir facilement.',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmez le nouveau mot de passe',
    hint: 'Utilisez au moins 8 caractères comprenant une majuscule, une minuscule, un chiffre et un symbole.',
    submit: 'Changer le mot de passe',
    success: 'Votre mot de passe a été mis à jour.',
    back: 'Retour au tableau de bord',
    errorMatch: 'Les mots de passe ne correspondent pas.',
    errorLength: 'Le mot de passe doit comporter au moins 8 caractères.',
    errorGeneric: 'Impossible de changer le mot de passe.'
  }
};

function ChangePassword() {
  const { language } = useContext(LanguageContext);
  const text = COPY[language] || COPY.en;
  const { ensureCaptcha, CaptchaOverlay } = useWafCaptcha();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const errorRef = useRef(null);
  const successRef = useRef(null);
  const capsCurrent = useCapsLockWarning();
  const capsNew = useCapsLockWarning();
  const capsConfirm = useCapsLockWarning();

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  useEffect(() => {
    if (success && successRef.current) {
      successRef.current.focus();
    }
  }, [success]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(text.errorMatch);
      return;
    }
    if (newPassword.length < 8) {
      setError(text.errorLength);
      return;
    }

    try {
      await ensureCaptcha();
      await axios.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err?.response?.data?.message || text.errorGeneric);
    }
  };

  if (success) {
    return (
      <>
        <CaptchaOverlay />
        <a href="#main-content" className="govuk-skip-link">
          {text.skip}
        </a>
        <div
          role="status"
          tabIndex={-1}
          className="govuk-panel govuk-panel--confirmation success-message"
          ref={successRef}
          id="main-content"
        >
          <p className="govuk-panel__title">{text.success}</p>
          <p className="govuk-body">
            <Link className="govuk-link" to="/dashboard">
              {text.back}
            </Link>
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <CaptchaOverlay />
      <a href="#main-content" className="govuk-skip-link">
        {text.skip}
      </a>
      <main className="govuk-main-wrapper govuk-width-container" role="main" id="main-content" tabIndex="-1">
        <h1 className="govuk-heading-l">{text.heading}</h1>
        <p className="govuk-hint">{text.intro}</p>
        <form onSubmit={handleSubmit} className="govuk-form-group" autoComplete="on">
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="current-password">
              {text.currentPassword}
            </label>
            <input
              className="govuk-input"
              id="current-password"
              name="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              {...capsCurrent.bind}
            />
            <CapsLockWarning show={capsCurrent.show} language={language} />
          </div>

          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="new-password">
              {text.newPassword}
            </label>
            <span className="govuk-hint">{text.hint}</span>
            <input
              className="govuk-input"
              id="new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              {...capsNew.bind}
            />
            <CapsLockWarning show={capsNew.show} language={language} />
          </div>

          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="confirm-password">
              {text.confirmPassword}
            </label>
            <input
              className="govuk-input"
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              {...capsConfirm.bind}
            />
            <CapsLockWarning show={capsConfirm.show} language={language} />
          </div>

          {error && (
            <div className="govuk-error-message error-message" role="alert" aria-live="assertive" tabIndex="-1" ref={errorRef}>
              {error}
            </div>
          )}

          <div className="govuk-button-group govuk-!-margin-top-4">
            <button type="submit" className="govuk-button">
              {text.submit}
            </button>
          </div>
        </form>
      </main>
    </>
  );
}

export default ChangePassword;
